"use client";

import React, { useState, useMemo } from "react";
import { Search, Funnel, Droplets, Waves, Fish } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "@/components/ui/pagination";
const fishDatabase = [{
  id: "f1",
  name: "Urban Pulse",
  scientific: "Commercial / 2024",
  category: "Commercial",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658650/img/urban_pulse_video.png",
  habitat: "High-energy street culture captured for global lifestyle brands."
}, {
  id: "f2",
  name: "The Silent Peak",
  scientific: "Documentary / 2023",
  category: "Documentary",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658650/img/silent_peak_doc.png",
  habitat: "An immersive journey into the heart of the alpine wilderness."
}, {
  id: "f3",
  name: "Neon Dreams",
  scientific: "Social / 2024",
  category: "Social",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658650/img/neon_dreams_social.png",
  habitat: "Short-form cinematic artistry designed for maximum engagement."
}, {
  id: "f4",
  name: "Velocity",
  scientific: "Commercial / 2024",
  category: "Commercial",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658650/img/velocity_automotive.png",
  habitat: "Precision automotive cinematography for luxury performance."
}, {
  id: "f5",
  name: "Echoes of Time",
  scientific: "Documentary / 2023",
  category: "Documentary",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658650/img/echoes_time_film.png",
  habitat: "Exploring forgotten histories through a modern cinematic lens."
}, {
  id: "f6",
  name: "Vogue Motion",
  scientific: "Social / 2024",
  category: "Social",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658650/img/vogue_motion_fashion.png",
  habitat: "High-fashion aesthetics optimized for vertical streaming platforms."
}, {
  id: "f7",
  name: "Steel & Glass",
  scientific: "Commercial / 2024",
  category: "Commercial",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658650/img/steel_glass_arch.png",
  habitat: "Architectural storytelling for world-class design firms."
}, {
  id: "f8",
  name: "The Last Artisan",
  scientific: "Documentary / 2024",
  category: "Documentary",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658650/img/last_artisan_craft.png",
  habitat: "A portrait of craftsmanship in a rapidly digital world."
}];
export default function FishArchive() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("Tutti");
  const filteredFish = useMemo(() => {
    return fishDatabase.filter(fish => {
      const matchesSearch = fish.name.toLowerCase().includes(searchQuery.toLowerCase()) || fish.scientific.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === "Tutti" || fish.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, activeCategory]);
  return <section data-section-id="224664" id="our-work" className="relative min-h-screen w-full bg-background py-24 overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] opacity-30 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-secondary/10 rounded-full blur-[150px] opacity-20 pointer-events-none" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        {}
        <div className="mb-12 flex flex-col items-center text-center space-y-4">
          <h2 className="font-default text-4xl md:text-7xl font-bold tracking-tighter text-foreground uppercase leading-none">
            SELECTED <span className="text-primary">WORKS</span>
          </h2>
          <p className="max-w-2xl text-muted-foreground text-lg font-default">
            Cinematic artistry for brands that demand attention. Explore our portfolio of commercial, social, and documentary content.
          </p>
        </div>

        {}
        <div className="mb-10 flex flex-col md:flex-row items-center justify-between gap-6 p-2 rounded-none bg-secondary/20 backdrop-blur-none border border-border shadow-none">
          
          {}
          <div className="relative w-full md:max-w-md group">
            <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
              <Search className="size-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
            </div>
            <input type="text" className="block w-full h-12 pl-11 pr-16 rounded-none bg-background border-b border-border text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none transition-all font-mono text-xs uppercase tracking-widest" placeholder="SEARCH PROJECTS..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-none border border-border bg-secondary text-[10px] font-mono text-muted-foreground uppercase tracking-wider">
                <span className="text-xs"></span> CMD K
              </span>
            </div>
          </div>

          {}
          <div className="w-full md:w-auto overflow-x-auto pb-2 md:pb-0 hide-scrollbar">
            <ToggleGroup type="single" value={activeCategory} onValueChange={value => {
            if (value) setActiveCategory(value);
          }} className="justify-start md:justify-end bg-secondary/40 p-1 rounded-none border border-border">
              <ToggleGroupItem value="Tutti" aria-label="All Projects" className="rounded-none px-4 font-mono text-xs uppercase tracking-widest data-[state=on]:bg-primary data-[state=on]:text-primary-foreground transition-all">
                <Funnel className="size-4 mr-2 opacity-70" />
                All
              </ToggleGroupItem>
              <ToggleGroupItem value="Commercial" aria-label="Commercial" className="rounded-none px-4 font-mono text-xs uppercase tracking-widest data-[state=on]:bg-primary data-[state=on]:text-primary-foreground transition-all">
                <Droplets className="size-4 mr-2 opacity-70" />
                Commercial
              </ToggleGroupItem>
              <ToggleGroupItem value="Social" aria-label="Social" className="rounded-none px-4 font-mono text-xs uppercase tracking-widest data-[state=on]:bg-primary data-[state=on]:text-primary-foreground transition-all">
                <Waves className="size-4 mr-2 opacity-70" />
                Social
              </ToggleGroupItem>
              <ToggleGroupItem value="Documentary" aria-label="Documentary" className="rounded-none px-4 font-mono text-xs uppercase tracking-widest data-[state=on]:bg-primary data-[state=on]:text-primary-foreground transition-all">
                <Fish className="size-4 mr-2 opacity-70" />
                Documentary
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        {}
        {filteredFish.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16">
            {filteredFish.map((fish, index) => <div key={fish.id} data-index={index} className="h-full">
                <Card className="group relative h-full overflow-hidden bg-card border-border rounded-none shadow-none transition-all duration-500 hover:bg-secondary/50 hover:border-primary/50 cursor-pointer">
                  {}
                  <div className="relative aspect-[16/9] overflow-hidden bg-black">
                    <img src={fish.image} alt={fish.name} className="object-cover w-full h-full opacity-70 grayscale transition-all duration-700 group-hover:scale-105 group-hover:opacity-100 group-hover:grayscale-0" />
                    {}
                    <div className="absolute top-0 left-0 px-3 py-1 bg-primary text-[10px] font-mono font-bold text-primary-foreground uppercase tracking-widest">
                      {fish.category}
                    </div>
                    
                    {}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent opacity-80 group-hover:opacity-40 transition-opacity duration-500" />
                  </div>

                  {}
                  <CardContent className="relative p-6 z-10">
                    <h3 className="font-default text-xl font-bold text-foreground mb-1 uppercase tracking-tighter transition-colors group-hover:text-primary">
                      {fish.name}
                    </h3>
                    
                    {}
                    <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-all duration-300 ease-in-out">
                      <div className="overflow-hidden">
                        <p className="font-mono text-[10px] text-primary pt-1 pb-3 uppercase tracking-widest">
                          {fish.scientific}
                        </p>
                      </div>
                    </div>

                    <p className="font-mono text-[11px] text-muted-foreground leading-relaxed mt-2 uppercase">
                      {fish.habitat}
                    </p>
                  </CardContent>

                  {}
                  <div className="absolute inset-0 border border-transparent group-hover:border-primary/30 rounded-none pointer-events-none transition-colors duration-500" />
                </Card>
              </div>)}
          </div> : <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="size-16 rounded-none bg-secondary flex items-center justify-center mb-4 border border-border">
              <Search className="size-8 text-muted-foreground" />
            </div>
            <h3 className="font-default text-xl font-bold text-foreground mb-2 uppercase tracking-tighter">No Projects Found</h3>
            <p className="font-mono text-xs text-muted-foreground uppercase">Adjust your filters or search parameters.</p>
            <Button variant="outline" className="mt-6 rounded-none border-border bg-transparent hover:bg-primary hover:text-primary-foreground font-mono text-xs uppercase tracking-widest" onClick={() => {
          setSearchQuery("");
          setActiveCategory("Tutti");
        }}>
              Reset Filters
            </Button>
          </div>}

        {}
        {filteredFish.length > 0 && <div className="mt-12 flex justify-center">
            <Pagination className="bg-secondary/20 backdrop-blur-none border border-border rounded-none p-1 w-auto inline-flex">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious href="#" className="hover:bg-primary hover:text-primary-foreground rounded-none font-mono text-[10px] uppercase" />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#" isActive className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono text-[10px]">1</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#" className="hover:bg-secondary text-muted-foreground hover:text-foreground rounded-none font-mono text-[10px]">2</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink href="#" className="hover:bg-secondary text-muted-foreground hover:text-foreground rounded-none font-mono text-[10px]">3</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationEllipsis className="text-muted-foreground" />
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext href="#" className="hover:bg-primary hover:text-primary-foreground rounded-none font-mono text-[10px] uppercase" />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>}
      </div>
    </section>;
}